import { useNavigate } from 'react-router-dom';
import { Card, Text, Badge, Group, Button, Avatar, Box } from '@mantine/core';
import { IconMapPin, IconStar, IconUser } from '@tabler/icons-react';
import FavoriteButton from './FavoriteButton';
import RatingStars from './RatingStars';

// Public seller card (contract §3.5) — shared by the browse grid and the
// favorites page. `onFavoriteToggled(nowFavorited)` bubbles up from the heart.
const SellerCard = ({ seller, onFavoriteToggled }) => {
  const navigate = useNavigate();

  return (
    <Card
      shadow="sm"
      padding="xl"
      radius="lg"
      withBorder
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        cursor: 'pointer',
        position: 'relative',
        transition: 'transform 0.15s, box-shadow 0.15s',
      }}
      onClick={() => navigate(`/princess/${seller.id}`)}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(159,56,204,0.18)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      {/* Favorite heart (signed-in only) */}
      <Box style={{ position: 'absolute', top: 10, right: 10 }}>
        <FavoriteButton
          sellerId={seller.id}
          initialFavorited={seller.is_favorited}
          onToggled={onFavoriteToggled}
        />
      </Box>

      {/* Profile Picture */}
      <Avatar
        size={110}
        radius="50%"
        color="pink"
        style={{ border: '3px solid #9f38cc', marginBottom: 12 }}
      >
        <IconUser size="2.5rem" />
      </Avatar>

      {/* Name + verified badge */}
      <Group gap="xs" justify="center" mb={2}>
        <Text fw={700} size="lg" c="grape">
          {seller.business_name}
        </Text>
        {seller.is_verified && (
          <Badge color="green" variant="light" size="sm">✓ Verified</Badge>
        )}
        {/* Profile boost (contract §4.21) */}
        {seller.is_featured && (
          <Badge color="yellow" variant="filled" size="sm" leftSection={<IconStar size="0.7rem" />}>
            Featured
          </Badge>
        )}
      </Group>

      {/* Review aggregate (contract §4.10) */}
      {seller.rating_count > 0 && (
        <Group justify="center" mb={2}>
          <RatingStars value={seller.rating_avg} count={seller.rating_count} />
        </Group>
      )}

      <Text size="sm" c="dimmed" mb="sm">@{seller.display_name}</Text>

      {/* Bio */}
      <Text size="sm" c="dark" lineClamp={3} mb="md" style={{ minHeight: 60 }}>
        {seller.business_description || 'This princess hasn\'t added a bio yet.'}
      </Text>

      {/* Location */}
      {(seller.business_city || seller.business_state) && (
        <Group gap={4} justify="center" mb="sm">
          <IconMapPin size="0.85rem" color="var(--mantine-color-gray-5)" />
          <Text size="xs" c="dimmed">
            {[seller.business_city, seller.business_state].filter(Boolean).join(', ')}
          </Text>
        </Group>
      )}

      {/* Stats */}
      <Group gap={6} justify="center" mb="md">
        {seller.average_rating > 0 && (
          <Badge size="xs" color="yellow" variant="light">
            ★ {seller.average_rating}/5
          </Badge>
        )}
        <Badge size="xs" color="pink" variant="light">
          {seller.total_orders} orders
        </Badge>
      </Group>

      <Button
        variant="light"
        color="grape"
        fullWidth
        mt="auto"
        leftSection={<IconUser size="1rem" />}
      >
        View Profile
      </Button>
    </Card>
  );
};

export default SellerCard;
